import {createSlice} from '@reduxjs/toolkit'
import {RootState} from '../store/Store'
import {assetApi} from './Asset.api'
import { IFilterDto, TSort } from '../../common/model/Core.model'
import { IAssetDto, IAssetFamily, IAssetArea } from './Asset.model'
import { signOut } from '../auth/Auth.slice'

type InitialState = {
  filtersSelection: IFilterDto[]
  assets: {
    error: string | undefined
    loading: boolean
    data: IAssetDto[]
    pagination?: {
      limit?: number
      sort?: TSort
      page?: string | undefined
    }
    filters?: IFilterDto[]
  }
  asset: {
    error: string | undefined
    loading: boolean
    data: IAssetDto | undefined
  }
  assetFamilies: {
    error: string | undefined
    loading: boolean
    data: IAssetFamily[]
  }
  assetAreas: {
    error: string | undefined
    loading: boolean
    data: IAssetArea[]
  }
}
const initialState = {
  filtersSelection: [],
  assets: {
    error: undefined,
    loading: false,
    data: [],
    pagination: {
      limit: 0,
      sort: 'ASC',
      page: undefined,
    },
    filters: undefined,
  },
  asset: {
    error: undefined,
    loading: false,
    data: undefined,
  },
  assetFamilies: {
    error: undefined,
    loading: false,
    data: [],
  },
  assetAreas: {
    error: undefined,
    loading: false,
    data: [],
  },
} as InitialState

export const assetSlice = createSlice({
  name: 'assets',
  initialState,
  reducers: {
    setAssetFiltersSelection: (state, action) => {
      state.filtersSelection = action.payload
    },
  },
  extraReducers(builder) {
    builder
      .addCase(signOut, () => initialState)
      //GetAssetFamily
      .addMatcher(assetApi.endpoints.assetFamilyGetAll.matchPending, (state) => {
        state.assetFamilies = {
          ...initialState.assetFamilies,
          error: undefined,
          loading: true,
        }
      })
      .addMatcher(assetApi.endpoints.assetFamilyGetAll.matchFulfilled, (state, action) => {
        state.assetFamilies = {
          ...initialState.assetFamilies,
          loading: false,
          data: action.payload.data,
        }
      })
      .addMatcher(assetApi.endpoints.assetFamilyGetAll.matchRejected, (state, action) => {
        state.assetFamilies = {
          ...initialState.assetFamilies,
          error: action.error.message,
          loading: false,
        }
      })
      //GetAssetArea
      .addMatcher(assetApi.endpoints.assetAreaGetAll.matchPending, (state) => {
        state.assetAreas = {
          ...initialState.assetAreas,
          error: undefined,
          loading: true,
        }
      })
      .addMatcher(assetApi.endpoints.assetAreaGetAll.matchFulfilled, (state, action) => {
        state.assetAreas = {
          ...initialState.assetAreas,
          loading: false,
          data: action.payload.data,
        }
      })
      .addMatcher(assetApi.endpoints.assetAreaGetAll.matchRejected, (state, action) => {
        state.assetAreas = {
          ...initialState.assetAreas,
          error: action.error.message,
          loading: false,
        }
      })
      //SearchAssets
      .addMatcher(assetApi.endpoints.assetSearch.matchPending, (state) => {
        state.assets = {
          ...initialState.assets,
          error: undefined,
          loading: true,
        }
      })
      .addMatcher(assetApi.endpoints.assetSearch.matchFulfilled, (state, action) => {
        state.assets = {
          error: undefined,
          loading: false,
          data: action.payload.data,
          pagination: action.payload.pagination,
          filters: action.payload.filters,
        }
      })
      .addMatcher(assetApi.endpoints.assetSearch.matchRejected, (state, action) => {
        state.assets = {
          ...initialState.assets,
          error: action.error.message,
          loading: false,
        }
      })
      //LoadMoreAssets
      .addMatcher(assetApi.endpoints.assetLoadMore.matchPending, (state) => {
        state.assets = {
          ...state.assets,
          error: undefined,
          loading: true,
        }
      })
      .addMatcher(assetApi.endpoints.assetLoadMore.matchFulfilled, (state, action) => {
        state.assets.data.push(...action.payload.data)
        state.assets = {
          ...state.assets,
          error: undefined,
          loading: false,
          pagination: action.payload.pagination,
          filters: action.payload.filters,
        }
      })
      .addMatcher(assetApi.endpoints.assetLoadMore.matchRejected, (state, action) => {
        state.assets = {
          ...state.assets,
          error: action.error.message,
          loading: false,
        }
      })
      //GetAssetById
      .addMatcher(assetApi.endpoints.assetGetById.matchPending, (state) => {
        state.asset = {
          ...initialState.asset,
          error: undefined,
          loading: true,
          data: undefined,
        }
      })
      .addMatcher(assetApi.endpoints.assetGetById.matchFulfilled, (state, action) => {
        state.asset = {
          ...initialState.asset,
          error: undefined,
          loading: false,
          data: action.payload,
        }
      })
      .addMatcher(assetApi.endpoints.assetGetById.matchRejected, (state, action) => {
        state.asset = {
          ...initialState.asset,
          error: action.error.message,
          loading: false,
        }
      })
      //CreateAsset
      .addMatcher(assetApi.endpoints.assetCreate.matchPending, (state) => {
        state.asset = {
          ...initialState.asset,
          error: undefined,
          loading: true,
        }
      })
      .addMatcher(assetApi.endpoints.assetCreate.matchFulfilled, (state, action) => {
        state.asset = {
          ...state.asset,
          error: undefined,
          loading: false,
          data: action.payload,
        }
      })
      .addMatcher(assetApi.endpoints.assetCreate.matchRejected, (state, action) => {
        state.asset = {
          ...state.asset,
          error: action.error.message,
          loading: false,
        }
      })
      //DeleteAssetById
      .addMatcher(assetApi.endpoints.assetDeleteById.matchPending, (state) => {
        state.asset = {
          ...initialState.asset,
          error: undefined,
          loading: true,
        }
      })
      .addMatcher(assetApi.endpoints.assetDeleteById.matchFulfilled, (state, action) => {
        state.asset = {
          ...initialState.asset,
          error: undefined,
          loading: false,
        }
      })
      .addMatcher(assetApi.endpoints.assetDeleteById.matchRejected, (state, action) => {
        state.asset = {
          ...initialState.asset,
          error: action.error.message,
          loading: false,
        }
      })
      //UpdateAssetById
      .addMatcher(assetApi.endpoints.assetUpdateById.matchPending, (state) => {
        state.asset = {
          ...state.asset,
          error: undefined,
          loading: true,
        }
      })
      .addMatcher(assetApi.endpoints.assetUpdateById.matchFulfilled, (state, action) => {
        state.asset = {
          ...state.asset,
          error: undefined,
          loading: false,
        }
      })
      .addMatcher(assetApi.endpoints.assetUpdateById.matchRejected, (state, action) => {
        state.asset = {
          ...state.asset,
          error: action.error.message,
          loading: false,
        }
      })
  },
})

export const {setAssetFiltersSelection} = assetSlice.actions
export default assetSlice.reducer
export const selectAssets = (state: RootState) => state.assetReducer.assets.data
export const selectAssetsLoading = (state: RootState) => state.assetReducer.assets.loading
export const selectAssetsPagination = (state: RootState) => state.assetReducer.assets.pagination
export const selectAssetsFilters = (state: RootState) => state.assetReducer.assets.filters
export const selectAssetFiltersSelection = (state: RootState) => state.assetReducer.filtersSelection

export const selectAsset = (state: RootState) => state.assetReducer.asset.data
export const selectAssetLoading = (state: RootState) => state.assetReducer.asset.loading

export const selectAssetFamilies = (state: RootState) => state.assetReducer.assetFamilies.data
export const selectAssetFamiliesLoading = (state: RootState) => state.assetReducer.assetFamilies.loading

export const selectAssetAreas = (state: RootState) => state.assetReducer.assetAreas.data
export const selectAssetAreasLoading = (state: RootState) => state.assetReducer.assetAreas.loading
